import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Calendar, Percent } from 'lucide-react';
import { EXCHANGE_RATE_TZS } from '../constants';
import { useData } from '../context/DataContext';

const Financing: React.FC = () => {
  const { openQuoteModal } = useData();
  const [price, setPrice] = useState(65000);
  const [downPayment, setDownPayment] = useState(15000);
  const [term, setTerm] = useState(48); // months
  const [interestRate, setInterestRate] = useState(12); // percent

  const [monthlyPayment, setMonthlyPayment] = useState(0);

  useEffect(() => {
    // Simple amortization formula
    const principal = price - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    if (principal > 0 && interestRate > 0) {
      const x = Math.pow(1 + monthlyRate, term);
      const monthly = (principal * x * monthlyRate) / (x - 1);
      setMonthlyPayment(monthly);
    } else {
      setMonthlyPayment(principal / term);
    }
  }, [price, downPayment, term, interestRate]);

  return (
    <div className="bg-neutral-900 min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="font-display text-5xl font-bold text-white uppercase tracking-wider mb-4">Financing Calculator</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">Estimate your monthly payments. Automax offers competitive rates and flexible terms for businesses across East Africa.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 bg-neutral-800 rounded-sm overflow-hidden shadow-2xl">

          {/* Inputs */}
          <div className="p-8 md:p-12 space-y-8">
            <div>
              <label className="flex items-center text-gray-300 text-sm font-bold uppercase tracking-wider mb-2">
                <DollarSign size={16} className="mr-2 text-automax-red" /> Vehicle Price (USD)
              </label>
              <input
                type="range"
                min="20000"
                max="200000"
                step="1000"
                value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-automax-red mb-2"
              />
              <div className="flex justify-between">
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(Number(e.target.value))}
                  className="bg-gray-50 border border-gray-600 text-gray-900 p-2 rounded-sm w-32 focus:border-automax-red outline-none font-bold"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center text-gray-300 text-sm font-bold uppercase tracking-wider mb-2">
                <DollarSign size={16} className="mr-2 text-automax-red" /> Down Payment (USD)
              </label>
              <input
                type="range"
                min="0"
                max={price * 0.8}
                step="1000"
                value={downPayment}
                onChange={(e) => setDownPayment(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-automax-red mb-2"
              />
              <div className="flex justify-between items-center text-gray-400 text-sm mt-1">
                <span className="font-bold text-white">${downPayment.toLocaleString()}</span>
                <div className="flex items-center bg-gray-50 rounded-sm px-3 py-2 border border-gray-600 focus-within:border-automax-red transition-colors">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={Math.round((downPayment / price) * 100)}
                    onChange={(e) => {
                      const val = Math.min(100, Math.max(0, Number(e.target.value)));
                      setDownPayment(Math.round((price * val) / 100));
                    }}
                    className="w-16 bg-transparent text-gray-900 text-right outline-none font-bold appearance-none text-lg"
                  />
                  <span className="ml-1 text-gray-500 font-bold">%</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="flex items-center text-gray-300 text-sm font-bold uppercase tracking-wider mb-2">
                  <Calendar size={16} className="mr-2 text-automax-red" /> Term
                </label>
                <select
                  value={term}
                  onChange={(e) => setTerm(Number(e.target.value))}
                  className="w-full bg-gray-50 border border-gray-600 text-gray-900 p-3 rounded-sm focus:border-automax-red outline-none font-bold"
                >
                  <option value="12">12 Months</option>
                  <option value="24">24 Months</option>
                  <option value="36">36 Months</option>
                  <option value="48">48 Months</option>
                  <option value="60">60 Months</option>
                </select>
              </div>
              <div>
                <label className="flex items-center text-gray-300 text-sm font-bold uppercase tracking-wider mb-2">
                  <Percent size={16} className="mr-2 text-automax-red" /> Interest Rate
                </label>
                <input
                  type="number"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="w-full bg-gray-50 border border-gray-600 text-gray-900 p-3 rounded-sm focus:border-automax-red outline-none font-bold"
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="bg-neutral-900 p-8 md:p-12 flex flex-col justify-center border-l border-gray-800">
            <div className="text-center mb-8">
              <p className="text-gray-400 text-sm uppercase tracking-widest mb-2">Estimated Monthly Payment</p>
              <div className="font-display text-7xl font-bold text-white">
                ${Math.round(monthlyPayment).toLocaleString()}
              </div>
              <p className="text-automax-red mt-2 font-mono">
                ~ TZS {(Math.round(monthlyPayment) * EXCHANGE_RATE_TZS).toLocaleString()}
              </p>
            </div>

            <div className="space-y-4 border-t border-gray-800 pt-8">
              <div className="flex justify-between text-gray-400 text-sm">
                <span>Loan Amount</span>
                <span className="text-white font-bold">${(price - downPayment).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-gray-400 text-sm">
                <span>Total Interest</span>
                <span className="text-white font-bold">${(Math.round((monthlyPayment * term) - (price - downPayment))).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-gray-400 text-sm">
                <span>Total Cost of Loan</span>
                <span className="text-white font-bold">${(Math.round(monthlyPayment * term)).toLocaleString()}</span>
              </div>
            </div>

            <button
              onClick={() => openQuoteModal('financing')}
              className="w-full mt-8 bg-automax-red hover:bg-red-700 text-white font-bold py-4 px-6 rounded-sm uppercase tracking-wider transition-all cursor-pointer"
            >
              Apply for Financing
            </button>
          </div>

        </div>
        <p className="text-center text-gray-500 text-xs mt-8">* This calculator provides estimates only. Actual rates and payments may vary based on credit approval and current offers.</p>
      </div>
    </div>
  );
};

export default Financing;
